import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils';

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: Variant;
    size?: Size;
    loading?: boolean;
    children: ReactNode;
}

const VARIANT_CLASSES: Record<Variant, string> = {
    primary: 'bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold disabled:bg-gray-400',
    secondary: 'bg-transparent border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 font-medium',
    danger: 'bg-brand-red hover:bg-red-700 text-white font-semibold',
    ghost: 'bg-transparent hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200',
};

const SIZE_CLASSES: Record<Size, string> = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2.5 text-sm',
    lg: 'px-6 py-3 text-base',
};

export function Button({
    variant = 'primary',
    size = 'md',
    loading = false,
    children,
    className,
    disabled,
    ...rest
}: ButtonProps) {
    return (
        <button
            className={cn(
                'inline-flex items-center justify-center rounded-lg active:translate-y-px transition-colors disabled:cursor-not-allowed disabled:opacity-70',
                VARIANT_CLASSES[variant],
                SIZE_CLASSES[size],
                className
            )}
            disabled={disabled || loading}
            {...rest}
        >
            {loading && (
                <span className="mr-2 inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
            )}
            {children}
        </button>
    );
}
